<!-- ADR 57 — shown when the workspace trial has ended and the free tier is
     disabled: the gateway locked the workspace (402 on the data plane). The
     owner can upgrade here (start_checkout is allow-listed while locked); other
     members just see the notice. After the grace window the workspace is
     permanently deleted. -->
<template>
  <div class="flex min-h-full items-center justify-center bg-canvas p-4">
    <div class="w-full max-w-lg rounded-2xl border border-line bg-surface p-7 shadow-sm">
      <div class="flex flex-col items-center text-center">
        <div class="relative size-9 shrink-0">
          <div class="absolute inset-0 rounded-lg bg-brand-600"></div>
          <div class="absolute -bottom-0.5 -right-0.5 size-3.5 rounded-sm bg-mark ring-2 ring-surface"></div>
        </div>
        <span class="mt-3 text-[15px] font-extrabold tracking-tight text-ink">Lymon</span>
        <div class="mt-4 flex items-center gap-2 text-danger">
          <Lock class="size-5" />
          <h1 class="text-xl font-semibold text-ink">{{ $t('upgradeWall.title') }}</h1>
        </div>
        <p class="mt-2 text-sm text-muted">
          {{ $t('upgradeWall.body', { workspace: auth.tenantName ?? $t('upgradeWall.thisWorkspace') }) }}
        </p>
        <p v-if="purgeDateLabel" class="mt-2 text-sm font-medium text-danger">
          {{ $t('upgradeWall.purgeWarning', { date: purgeDateLabel }) }}
        </p>
      </div>

      <!-- Owner: purchasable plans with checkout. Members: contact the owner. -->
      <div v-if="auth.isOwner" class="mt-6">
        <div v-if="billingEnabled" class="grid gap-3 sm:grid-cols-2">
          <button
            v-for="p in purchasablePlans"
            :key="p.id"
            class="rounded-xl border border-line p-4 text-left transition-colors hover:border-brand-600 disabled:opacity-60"
            :disabled="busy"
            @click="upgrade(p.id)"
          >
            <div class="text-base font-semibold text-ink">{{ p.display }}</div>
            <div class="mt-1 text-xs text-muted">{{ $t('upgradeWall.choosePlan') }}</div>
          </button>
        </div>
        <a
          v-else
          :href="salesMailto"
          class="block rounded-xl border border-line p-4 text-center text-sm font-medium text-ink transition-colors hover:border-brand-600"
        >{{ $t('upgradeWall.contactSales') }}</a>
        <p v-if="error" class="mt-3 text-center text-sm text-danger">{{ error }}</p>
      </div>
      <p v-else class="mt-6 rounded-xl border border-line bg-canvas p-4 text-center text-sm text-muted">
        {{ $t('upgradeWall.memberNote') }}
      </p>

      <!-- Escape hatch: switch to another workspace the user belongs to. -->
      <div v-if="otherWorkspaces.length" class="mt-6 border-t border-line pt-5">
        <p class="mb-2.5 text-center text-xs font-semibold uppercase tracking-wide text-muted">
          {{ $t('upgradeWall.switchTitle') }}
        </p>
        <ul class="space-y-2">
          <li v-for="ws in otherWorkspaces" :key="ws.tenant_id">
            <button
              type="button"
              class="flex w-full items-center justify-between gap-2 rounded-xl border border-line px-4 py-3 text-left transition-colors hover:border-brand-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-line"
              :disabled="ws.locked"
              @click="auth.switchTenant(ws.tenant_id)"
            >
              <span class="min-w-0 flex-1">
                <span class="block truncate text-sm font-medium text-ink">{{ ws.name || ws.tenant_id }}</span>
                <span class="block text-xs text-faint">{{ ws.role }}</span>
              </span>
              <span v-if="ws.locked" class="flex shrink-0 items-center gap-1 text-xs font-medium text-danger">
                <Lock class="size-3.5" />{{ $t('upgradeWall.lockedLabel') }}
              </span>
              <ChevronRight v-else class="size-4 shrink-0 text-faint" />
            </button>
          </li>
        </ul>
      </div>

      <div class="mt-6 flex items-center justify-center">
        <Button variant="ghost" size="sm" @click="auth.logout()">{{ $t('upgradeWall.signOut') }}</Button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { Lock, ChevronRight } from 'lucide-vue-next';
import { useAuthStore } from '../stores/auth';
import Button from '../components/Button.vue';
import { callTool } from '../api/client';
import { currentLocale } from '../i18n';

interface PlanRow { id: string; display: string; kind: string }
interface CatalogResp { plans: PlanRow[]; billing_enabled?: boolean }

const auth = useAuthStore();
const busy = ref(false);
const error = ref<string | null>(null);
const catalog = ref<CatalogResp | null>(null);
const PURCHASABLE = new Set(['starter', 'pro']);
const salesMailto = 'mailto:info@lybits.es?subject=Lymon%20upgrade';

const billingEnabled = computed(() => catalog.value?.billing_enabled === true);
const purchasablePlans = computed(() =>
  (catalog.value?.plans ?? []).filter((p) => PURCHASABLE.has(p.id)),
);
// The user may belong to other workspaces that are NOT locked — offer them as
// an escape hatch (App.vue only mounts this wall, never the TopBar switcher).
const otherWorkspaces = computed(() =>
  auth.tenants.filter((t) => t.tenant_id !== auth.tenantId),
);
const purgeDateLabel = computed(() => {
  if (!auth.purgeAfter) return null;
  try {
    return new Date(auth.purgeAfter).toLocaleDateString(currentLocale() === 'es' ? 'es-ES' : 'en-GB', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch { return null; }
});

onMounted(async () => {
  // Load the user's other workspaces so a locked-out user can jump to one that
  // still works, instead of being stranded (the TopBar switcher never mounts here).
  void auth.loadTenants();
  try { catalog.value = await callTool<CatalogResp>('get_plan_catalog', {}); } catch { /* keep the wall usable */ }
});

async function upgrade(plan: string): Promise<void> {
  error.value = null;
  busy.value = true;
  try {
    const r = await callTool<{ ok: boolean; url?: string; error?: string }>('start_checkout', { plan });
    if (r.ok && r.url) { window.location.href = r.url; return; }
    error.value = r.error ?? 'checkout_failed';
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err);
  } finally {
    busy.value = false;
  }
}
</script>
